import React from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Alert,
  Button,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import PricesList from "./PricesList/PricesList";
import { usePricesManagement } from "./usePricesManagement";
import DeletePriceDialog from "./DeletePriceDialog/DeletePriceDialog";
import EditPriceDialog from "./EditPriceDialog/EditPriceDialog";
import CreatePriceDialog from "./CreatePriceDialog/CreatePriceDialog";

export const PriceManagement: React.FC = () => {
  const intl = useIntl();
  const {
    prices,
    totalPrices,
    error,
    deleteDialogOpen,
    editDialogOpen,
    createDialogOpen,
    priceToEdit,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchTerm,
    deleteError,
    fetchPriceData,
    handleDeletePrice,
    confirmDeletePrice,
    handleEditPrice,
    handleCreatePrice,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRequestSort,
    handleSearchChange,
    setDeleteDialogOpen,
    setEditDialogOpen,
    setCreateDialogOpen,
    setDeleteError,
    onPriceUpdate,
  } = usePricesManagement();

  React.useEffect(() => {
    fetchPriceData();
  }, [fetchPriceData]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" component="div">
                <FormattedMessage id="admin.dashboard.prices" />
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SearchIcon sx={{ mr: 1 }} />
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder={intl.formatMessage({
                    id: "admin.dashboard.searchPlaceholder",
                  })}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            <FormattedMessage id="admin.dashboard.createPrice" />
          </Button>
        </Box>
        <PricesList
          prices={prices}
          totalPrices={totalPrices}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          order={order}
          onRequestSort={handleRequestSort}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onEditPrice={handleEditPrice}
          onDeletePrice={handleDeletePrice}
        />
      </Paper>
      <DeletePriceDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteError(null);
        }}
        onConfirm={confirmDeletePrice}
        error={deleteError}
      />
      <EditPriceDialog
        open={editDialogOpen}
        priceToEdit={priceToEdit}
        onClose={() => setEditDialogOpen(false)}
        onSave={onPriceUpdate}
      />
      <CreatePriceDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreatePrice}
      />
    </Box>
  );
};

export default PriceManagement;
